import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, Plus, Star, ChevronDown } from 'lucide-react';
import { DraftPlayer } from '@/types/draftPlayer';
import { cn } from '@/lib/utils';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import PlayerStatsExpand from '@/components/draft/PlayerStatsExpand';
import { useSport } from '@/contexts/SportContext';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

const EXPAND_HINT_KEY = 'draft-expand-hint-seen';

interface PlayerListProps {
  players: DraftPlayer[];
  queuedPlayerIds: string[];
  onDraftPlayer: (playerId: string) => void;
  onQueuePlayer: (playerId: string) => void;
  onRemoveFromQueue: (playerId: string) => void;
  isOnTheClock: boolean;
  draftedPlayerIds: string[];
  fakeInjuryPlayerIds?: string[];
  isGuessWhoActive?: boolean;
  myNextPickNumber?: number;
  myNextPickRound?: number;
  currentPickNumber?: number;
  fullHeight?: boolean;
  isMobileOverride?: boolean;
  /** Map of player_id -> team name that drafted them (for search attribution) */
  draftedByTeamMap?: Map<string, string>;
}

const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400',
  RB: 'bg-green-500/20 text-green-400',
  WR: 'bg-blue-500/20 text-blue-400',
  TE: 'bg-orange-500/20 text-orange-400',
  K: 'bg-purple-500/20 text-purple-400',
  DEF: 'bg-yellow-500/20 text-yellow-400',
  // MLB positions
  C: 'bg-amber-500/20 text-amber-400',
  '1B': 'bg-green-500/20 text-green-400',
  '2B': 'bg-blue-500/20 text-blue-400',
  '3B': 'bg-red-500/20 text-red-400',
  SS: 'bg-purple-500/20 text-purple-400',
  OF: 'bg-cyan-500/20 text-cyan-400',
  SP: 'bg-orange-500/20 text-orange-400',
  RP: 'bg-pink-500/20 text-pink-400',
  DH: 'bg-yellow-500/20 text-yellow-400',
  UTIL: 'bg-teal-500/20 text-teal-400',
};

const PlayerList = ({
  players,
  queuedPlayerIds,
  onDraftPlayer,
  onQueuePlayer,
  onRemoveFromQueue,
  isOnTheClock,
  draftedPlayerIds,
  fakeInjuryPlayerIds = [],
  isGuessWhoActive = false,
  myNextPickNumber,
  myNextPickRound,
  currentPickNumber,
  fullHeight = false,
  isMobileOverride,
  draftedByTeamMap = new Map(),
}: PlayerListProps) => {
  const { activeSport } = useSport();
  const { toast } = useToast();
  const isMobileHook = useIsMobile();
  const isMobile = isMobileOverride ?? isMobileHook;
  const isMLB = activeSport === 'mlb';
  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('ALL');
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);
  const [confirmDraftPlayer, setConfirmDraftPlayer] = useState<DraftPlayer | null>(null);
  const confirmDraftRef = useRef<DraftPlayer | null>(null);
  const hintShown = useRef(false);
  const [showFirstRowPulse, setShowFirstRowPulse] = useState(false);
  const availablePlayers = players.filter(p => !draftedPlayerIds.includes(p.id));
  // When searching, also include drafted players so users can see who owns them
  const isSearching = search.length > 0;

  // On mobile, show confirmation modal; on desktop, draft directly
  const handleDraft = (player: DraftPlayer) => {
    if (!isOnTheClock) return;
    if (isMobile) {
      confirmDraftRef.current = player;
      setConfirmDraftPlayer(player);
    } else {
      onDraftPlayer(player.id);
    }
  };

  // One-time expand hint: pulse first chevron + mobile toast
  useEffect(() => {
    if (hintShown.current || availablePlayers.length === 0) return;
    const seen = localStorage.getItem(EXPAND_HINT_KEY);
    if (seen) return;
    hintShown.current = true;
    setShowFirstRowPulse(true);
    // Mobile toast hint
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      toast({ title: 'Tap a player to see stats', duration: 3000 });
    }
    localStorage.setItem(EXPAND_HINT_KEY, '1');
    // Stop pulsing after 4 seconds
    const timer = setTimeout(() => setShowFirstRowPulse(false), 4000);
    return () => clearTimeout(timer);
  }, [availablePlayers.length, toast]);

  // Use all players (including drafted) when searching, available-only otherwise
  const playerPool = isSearching ? players : availablePlayers;
  const filteredPlayers = playerPool.filter(player => {
    // When Guess Who is active, skip search/filter — show all available shuffled
    if (isGuessWhoActive) return true;
    const matchesSearch = !isSearching || player.name.toLowerCase().includes(search.toLowerCase()) ||
                          player.team.toLowerCase().includes(search.toLowerCase());
    const rawElig = player.position_eligibility;
    const eligArr = Array.isArray(rawElig) ? rawElig : rawElig && typeof rawElig === 'object' ? Object.keys(rawElig) : [];
    const eligiblePositions = new Set([player.position, ...eligArr]);
    let matchesPosition = positionFilter === 'ALL';
    if (!matchesPosition) {
      if (positionFilter === 'HITTERS') {
        matchesPosition = ['C', '1B', '2B', '3B', 'SS', 'OF', 'DH', 'UTIL'].some(p => eligiblePositions.has(p));
      } else if (positionFilter === 'PITCHERS') {
        matchesPosition = ['SP', 'RP', 'P'].some(p => eligiblePositions.has(p));
      } else if (positionFilter === 'IF') {
        matchesPosition = ['1B', '2B', '3B', 'SS'].some(p => eligiblePositions.has(p));
      } else {
        matchesPosition = eligiblePositions.has(positionFilter);
      }
    }
    return matchesSearch && matchesPosition;
  });

  // Shuffle deterministically when Guess Who is active (seeded by player count to stay stable within a pick)
  const shuffledPlayers = useMemo(() => {
    if (!isGuessWhoActive) return null;
    const arr = [...filteredPlayers];
    // Fisher-Yates shuffle with simple seed based on array length
    let seed = arr.length * 2654435761;
    const nextRand = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(nextRand() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [isGuessWhoActive, filteredPlayers]);

  const sortedPlayers = isGuessWhoActive ? (shuffledPlayers || filteredPlayers) : [...filteredPlayers].sort((a, b) => a.rank - b.rank);

  const toggleExpand = (playerId: string) => {
    setExpandedPlayerId(prev => prev === playerId ? null : playerId);
  };

  return (
    <ActiveNewsProvider sport="mlb" keys={sortedPlayers.map(p => p.name)}>
    <Card className={cn("glass-card", fullHeight ? "h-full flex flex-col" : "lg:h-full")}>
      <CardHeader className={cn("pb-3", fullHeight && "shrink-0")}>
        <CardTitle className="text-lg flex items-center gap-2">
          {isMLB ? '⚾' : '🏈'} {isSearching ? 'Players' : 'Available Players'}
          <Badge variant="outline" className="ml-auto">{availablePlayers.length} available</Badge>
        </CardTitle>
        {isGuessWhoActive && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-warning/20 border border-warning/40 text-warning text-sm font-medium">
            <span className="text-lg">👀</span>
            Guess Who: pick by face only
          </div>
        )}
        {!isGuessWhoActive && <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-muted/30"
            />
          </div>
          <Tabs value={positionFilter} onValueChange={setPositionFilter}>
            {isMLB ? (
              <>
                <TabsList className="grid grid-cols-5 h-8 w-full">
                  <TabsTrigger value="ALL" className="text-[13px] px-1 h-full">All</TabsTrigger>
                  <TabsTrigger value="HITTERS" className="text-[13px] px-1 h-full">Hitters</TabsTrigger>
                  <TabsTrigger value="PITCHERS" className="text-[13px] px-1 h-full">Pitchers</TabsTrigger>
                  <TabsTrigger value="IF" className="text-[13px] px-1 h-full">IF</TabsTrigger>
                  <TabsTrigger value="OF" className="text-[13px] px-1 h-full">OF</TabsTrigger>
                </TabsList>
                <TabsList className="grid grid-cols-8 h-8 w-full mt-1">
                  <TabsTrigger value="C" className="text-[13px] px-0.5 h-full">C</TabsTrigger>
                  <TabsTrigger value="1B" className="text-[13px] px-0.5 h-full">1B</TabsTrigger>
                  <TabsTrigger value="2B" className="text-[13px] px-0.5 h-full">2B</TabsTrigger>
                  <TabsTrigger value="3B" className="text-[13px] px-0.5 h-full">3B</TabsTrigger>
                  <TabsTrigger value="SS" className="text-[13px] px-0.5 h-full">SS</TabsTrigger>
                  <TabsTrigger value="SP" className="text-[13px] px-0.5 h-full">SP</TabsTrigger>
                  <TabsTrigger value="RP" className="text-[13px] px-0.5 h-full">RP</TabsTrigger>
                  <TabsTrigger value="DH" className="text-[13px] px-0.5 h-full">DH</TabsTrigger>
                </TabsList>
              </>
            ) : (
              <TabsList className="grid grid-cols-7 h-8">
                <TabsTrigger value="ALL" className="text-xs px-2">All</TabsTrigger>
                <TabsTrigger value="QB" className="text-xs px-2">QB</TabsTrigger>
                <TabsTrigger value="RB" className="text-xs px-2">RB</TabsTrigger>
                <TabsTrigger value="WR" className="text-xs px-2">WR</TabsTrigger>
                <TabsTrigger value="TE" className="text-xs px-2">TE</TabsTrigger>
                <TabsTrigger value="K" className="text-xs px-2">K</TabsTrigger>
                <TabsTrigger value="DEF" className="text-xs px-2">DEF</TabsTrigger>
              </TabsList>
            )}
          </Tabs>
        </div>}
      </CardHeader>
      <CardContent className={cn("p-0", fullHeight && "flex-1 min-h-0")}>
        <div className={cn(
          "overflow-y-auto",
          fullHeight
            ? "h-full"
            : "min-h-[200px] h-auto max-h-[45vh] lg:max-h-none lg:h-[calc(100vh-280px)]"
        )}>
          <div className="space-y-1 p-4 pt-0">
            {sortedPlayers.map((player, index) => {
              const isDrafted = draftedPlayerIds.includes(player.id);
              const draftedByTeam = draftedByTeamMap.get(player.id);
              const isQueued = !isDrafted && queuedPlayerIds.includes(player.id);
              const isFakeInjured = !isDrafted && fakeInjuryPlayerIds.includes(player.id);
              const isExpanded = expandedPlayerId === player.id;

              // Show "YOUR PICK" divider where the user's next pick falls in ADP order
              const picksUntilMyTurn = (myNextPickNumber && currentPickNumber)
                ? myNextPickNumber - currentPickNumber
                : null;
              const showYourPickDivider = picksUntilMyTurn !== null && picksUntilMyTurn > 0 && index === picksUntilMyTurn;

              return (
                <div key={player.id}>
                {showYourPickDivider && (
                  <div className="flex items-center gap-2 py-2 px-1 my-1">
                    <div className="flex-1 h-px bg-orange-500/50" />
                    <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider whitespace-nowrap">
                      Your Pick (R{myNextPickRound},P{myNextPickNumber})
                    </span>
                    <div className="flex-1 h-px bg-orange-500/50" />
                  </div>
                )}
                <div
                  className={cn(
                    "rounded-lg transition-all px-2 py-1.5 sm:p-3",
                    isDrafted && "opacity-50",
                    isQueued && "bg-primary/10 border border-primary/30",
                    isFakeInjured && "bg-red-500/10 border border-red-500/30",
                    isExpanded && !isQueued && !isFakeInjured && "bg-muted/20 border border-border",
                    !isDrafted && !isQueued && !isFakeInjured && !isExpanded && "hover:bg-muted/40"
                  )}
                >
                  {/* === PORTRAIT: Row 1 === */}
                  <div className="flex items-center gap-2 w-full sm:hidden">
                    <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0 overflow-hidden">
                      {player.headshot_url ? (
                        <img src={player.headshot_url} alt={isGuessWhoActive ? '???' : player.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-lg">?</div>
                      )}
                    </div>
                    {!isGuessWhoActive ? (
                      <>
                        <Badge className={cn("text-[10px] flex-shrink-0", positionColors[player.position])}>{formatPositionDisplay(player.position, player.position_eligibility)}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium text-sm">{player.name}</p>
                          <p className="truncate text-[10px] text-muted-foreground">
                            {player.mlb_team_name || player.team} · ADP: #{player.adp}
                            {player.projectedTrashPoints > 0 && <span className="text-orange-400 font-semibold"> · {player.projectedTrashPoints.toFixed(0)} pts</span>}
                          </p>
                          {isDrafted && draftedByTeam && (
                            <p className="text-[10px] text-amber-400 font-medium truncate">Drafted by {draftedByTeam}</p>
                          )}
                        </div>
                        <PlayerNewsIcon playerName={player.name} />
                        {!isDrafted && <div
                          className={cn("flex-shrink-0 cursor-pointer p-1", showFirstRowPulse && sortedPlayers[0]?.id === player.id && "animate-pulse")}
                          onClick={() => toggleExpand(player.id)}
                        >
                          <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded ? "rotate-180 text-primary" : "text-muted-foreground/80")} />
                        </div>}
                      </>
                    ) : (
                      <span className="font-medium text-muted-foreground italic flex-1">???</span>
                    )}
                  </div>

                  {/* === PORTRAIT: Row 2 — always visible (hide for drafted players) === */}
                  {!isDrafted && <div className="flex items-center justify-end gap-2 mt-0.5 sm:hidden">
                    <button
                      className="flex-shrink-0 p-1"
                      onClick={() => isQueued ? onRemoveFromQueue(player.id) : onQueuePlayer(player.id)}
                      title={isQueued ? "Remove from queue" : "Add to queue"}
                    >
                      <Star className={cn("w-4 h-4", isQueued ? "fill-primary text-primary" : "text-muted-foreground")} />
                    </button>
                    <button
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0 flex items-center gap-1 transition-opacity",
                        isOnTheClock
                          ? "bg-orange-500 hover:bg-orange-600 text-white"
                          : "bg-muted text-muted-foreground opacity-50 pointer-events-none"
                      )}
                      disabled={!isOnTheClock}
                      onClick={() => handleDraft(player)}
                    >
                      <Plus className="w-3 h-3" />
                      Draft
                    </button>
                  </div>}

                  {/* === LANDSCAPE / DESKTOP: Single Row === */}
                  <div className="hidden sm:flex items-center gap-2 w-full">
                    <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0 overflow-hidden">
                      {player.headshot_url ? (
                        <img src={player.headshot_url} alt={isGuessWhoActive ? '???' : player.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-lg">?</div>
                      )}
                    </div>
                    {!isGuessWhoActive ? (
                      <>
                        <Badge className={cn("text-xs flex-shrink-0", positionColors[player.position])}>{formatPositionDisplay(player.position, player.position_eligibility)}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium">{player.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {player.mlb_team_name || player.team}
                            {player.byeWeek > 0 && <> · Bye: {player.byeWeek}</>}
                            {' '}· ADP: #{player.adp}
                            {player.projectedTrashPoints > 0 && <span className="text-orange-400 font-semibold"> · {player.projectedTrashPoints.toFixed(0)} pts</span>}
                          </p>
                          {isDrafted && draftedByTeam && (
                            <p className="text-[10px] text-amber-400 font-medium truncate">Drafted by {draftedByTeam}</p>
                          )}
                        </div>
                        {isFakeInjured && <Badge className="bg-red-600/90 text-white text-[10px] px-1.5 py-0 flex-shrink-0">IL</Badge>}
                        {isDrafted && <Badge className="bg-muted text-muted-foreground text-[10px] px-1.5 py-0 flex-shrink-0">Drafted</Badge>}
                        <PlayerNewsIcon playerName={player.name} />
                        {!isDrafted && <div
                          className={cn("flex-shrink-0 cursor-pointer p-1", showFirstRowPulse && sortedPlayers[0]?.id === player.id && "animate-pulse")}
                          onClick={() => toggleExpand(player.id)}
                        >
                          <ChevronDown className={cn("w-4.5 h-4.5 transition-transform", isExpanded ? "rotate-180 text-primary" : "text-muted-foreground/80")} />
                        </div>}
                      </>
                    ) : (
                      <span className="font-medium text-muted-foreground italic flex-1">???</span>
                    )}
                    {!isDrafted && <>
                    <button
                      className="flex-shrink-0 p-1"
                      onClick={() => isQueued ? onRemoveFromQueue(player.id) : onQueuePlayer(player.id)}
                      title={isQueued ? "Remove from queue" : "Add to queue"}
                    >
                      <Star className={cn("w-4 h-4", isQueued ? "fill-primary text-primary" : "text-muted-foreground")} />
                    </button>
                    <button
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0 flex items-center gap-1 transition-opacity",
                        isOnTheClock
                          ? "bg-orange-500 hover:bg-orange-600 text-white"
                          : "bg-muted text-muted-foreground opacity-50 pointer-events-none"
                      )}
                      disabled={!isOnTheClock}
                      onClick={() => handleDraft(player)}
                    >
                      <Plus className="w-3 h-3" />
                      Draft
                    </button>
                    </>}
                  </div>

                  {/* Expandable stats */}
                  {isExpanded && !isGuessWhoActive && (
                    <div className="mt-2">
                      <PlayerStatsExpand
                        playerId={player.id}
                        position={player.position}
                        projectedTrashPoints={player.projectedTrashPoints}
                      />
                    </div>
                  )}
                </div>
                </div>
              );
            })}
            {sortedPlayers.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No players found</p>
                <p className="text-sm">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Mobile draft confirmation modal */}
      <AlertDialog
        open={!!confirmDraftPlayer}
        onOpenChange={(open) => {
          if (!open) setConfirmDraftPlayer(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Draft {confirmDraftPlayer?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDraftPlayer && (
                <span className="flex items-center gap-2">
                  <Badge className={cn("text-xs", positionColors[confirmDraftPlayer.position])}>
                    {formatPositionDisplay(confirmDraftPlayer.position, confirmDraftPlayer.position_eligibility)}
                  </Badge>
                  {confirmDraftPlayer.mlb_team_name || confirmDraftPlayer.team}
                  {confirmDraftPlayer.projectedTrashPoints > 0 && (
                    <span className="text-orange-400 font-semibold">
                      {confirmDraftPlayer.projectedTrashPoints.toFixed(0)} pts
                    </span>
                  )}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const player = confirmDraftRef.current;
                if (player) {
                  onDraftPlayer(player.id);
                  confirmDraftRef.current = null;
                }
              }}
            >
              Confirm Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
    </ActiveNewsProvider>
  );
};

export default PlayerList;
